// Decides whether a calendar assignment pill shows a complete/revert
// checkmark, and which way it toggles. Pure + tested so the matrix (gutter
// vs insulation-phase vs estimate, by job status) can't silently drift.
//
// Gutter installs have no per-assignment completion — completion IS the job
// status transition (scheduled <-> installed), so `isRevert` keys off
// jobStatus. Insulation phases complete per-phase (their own completed_at),
// so `isRevert` keys off the assignment's own completedAt. Estimates also
// complete per-assignment (own completed_at — the "crew did the measure
// visit" marker; no job-status change), so they key off completedAt too;
// hidden only on post-install-locked / disqualified jobs. Invoiced/completed
// INSTALL jobs hide the toggle — those reverts go through the stepper
// (invoice-date modal / confirm dialog), which the calendar must not bypass.

export type InstallToggle =
  | { show: false }
  | { show: true; kind: 'gutter' | 'phase' | 'estimate'; isRevert: boolean };

export function installToggleState(a: {
  purpose: 'install' | 'estimate';
  /** CalendarAssignmentRow.job.status — typed string upstream; compared to
   *  the status literals here. */
  jobStatus: string;
  /** assignment.phase != null → insulation phase visit. */
  isPhase: boolean;
  /** assignment.completedAt != null. */
  assignmentCompleted: boolean;
  /** True when the job already has a purpose='install' assignment. Used to
   *  lock a COMPLETED estimate's revert: once the install is scheduled off the
   *  estimate, reverting the estimate would rewrite history. */
  hasInstallAssignment: boolean;
}): InstallToggle {
  if (a.purpose === 'estimate') {
    // Per-assignment completion marker, decoupled from job status. Hidden only
    // when the job is post-install-locked or disqualified (read-only / handled
    // elsewhere); the worker rejects those too.
    if (
      a.jobStatus === 'installed' ||
      a.jobStatus === 'invoiced' ||
      a.jobStatus === 'completed' ||
      a.jobStatus === 'disqualified'
    ) {
      return { show: false };
    }
    // Once an install is scheduled, a COMPLETED estimate can't be reverted
    // (the estimate is what the install was scheduled off of). A not-yet-
    // completed estimate can still be marked done. Server enforces the same.
    if (a.assignmentCompleted && a.hasInstallAssignment) {
      return { show: false };
    }
    return { show: true, kind: 'estimate', isRevert: a.assignmentCompleted };
  }
  // ── installs (unchanged) ──
  // Only the two plain-mutate transitions are exposed here. Hidden on
  // invoiced/completed (and created/estimated/quoted/accepted/disqualified) so
  // the decorated transitions stay on the stepper.
  if (a.jobStatus !== 'scheduled' && a.jobStatus !== 'installed') return { show: false };
  const kind = a.isPhase ? 'phase' : 'gutter';
  const isRevert = a.isPhase ? a.assignmentCompleted : a.jobStatus === 'installed';
  return { show: true, kind, isRevert };
}
